/**
 *  Copyright (c) 2022-present TVM Engineering Authors.
 *
 */

// eslint-disable-next-line import/no-cycle
import { fetchLanguage } from "../services/index";
import types from "../actions/types";
import i18n from "../services/i18n";
import SideBarConfig from "../configs/SideBarConfig";

const localLang = localStorage.getItem("i18nextLng");
const lang = localLang !== null && localLang !== "en-US" ? localLang : "en";

const INITIAL_STATE = {
  isOnline: false,
  notificationStatus: {
    visible: false,
    text: "Test",
    notificationType: "",
    autohide: false,
    severity: null,
  },
  notifications: [
    { title: "Your today's revenue is CAD 125 from 2 orders", type: "revenue", date: "Last week" },
    {
      title: "Your today's top products is Crocs UNISEX Clogs (Demo)...",
      type: "product",
      date: "Last week",
    },
    {
      title: "Your today's top customer is Kelly (Demo). Reach out to Kelly to send thank you. ",
      type: "customer",
      date: "Last week",
    },
    {
      title:
        "Hi Testing for Client, Thank you for choosing Orderhive. " +
        "Click here to get started with few basic guidelines and onboarding tips.",
      type: "welcome",
      date: "Last week",
    },
  ],
  searchResults: [],
  lastSelectedEntry: null,
  settingsVersion: 1,
  isLoading: false,
  isCookies: false,
  error: null,
  userId: null,
  loggedIn: false,
  contentHash: "",
  isUpdateInProgress: false,
  isUpdateAvailable: false,
  isCreateUpdateProjectOpened: false,
  isSideBarOpened: !SideBarConfig.config.isAlwaysOpened,
  checkForUpdates: true,
  showMainMenu: false,
  interfaceLanguage: lang,
  currentTheme: "light",
  enableGlobalKeyboardShortcuts: true,
  supportedThemes: ["light", "dark"],
  displayWidth: "",
  addUserDialogOpened: false,
  editUserDialogOpened: false,
  settingsDialogOpened: false,
  entryFiles: [],
  supportedLanguages: [
    {
      iso: "en",
      title: "English",
    },
    {
      iso: "de",
      title: "Deutsch (German)",
    },
    // {
    //   iso: 'bg',
    //   title: 'Български (Bulgarian)'
    // },
  ],
};

export default function appReducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case types.DEVICE_IS_LOADING: {
      return { ...state, isLoading: action.payload, error: null };
    }
    case types.DEVICE_ONLINE: {
      return { ...state, isOnline: true, error: null };
    }
    case types.DEVICE_OFFLINE: {
      return { ...state, isOnline: false, error: null };
    }
    case types.SET_LANGUAGE: {
      i18n.changeLanguage(action.language);
      i18n.loadLanguages(action.language);
      return { ...state, interfaceLanguage: action.language };
    }
    case types.SET_COUNTRY: {
      return { ...state, userCountry: action.country };
    }
    case types.SET_DISPLAYSIZING: {
      return { ...state, displaySizing: action.sizing };
    }
    case types.SET_NOTIFICATION_STATUS: {
      // console.log('SET_NOTIFICATION_STATUS:');
      // console.log(action);
      return {
        ...state,
        notificationStatus: {
          visible: action.visible,
          text: action.text,
          notificationType: action.notificationType,
          autohide: action.autohide,
          severity: action.severity,
        },
      };
    }
    case types.SET_NOTIFICATION: {
      return {
        ...state,
        notification: action.notification,
      };
    }
    case types.TOGGLE_ADD_USER_DIALOG: {
      return { ...state, addUserDialogOpened: !state.addUserDialogOpened };
    }
    case types.TOGGLE_EDIT_USER_DIALOG: {
      return { ...state, editUserDialogOpened: !state.editUserDialogOpened };
    }
    case types.TOGGLE_INVITE_USER_DIALOG: {
      return { ...state, inviteUserDialogOpened: !state.inviteUserDialogOpened };
    }
    case types.TOGGLE_SETTINGS_DIALOG: {
      return { ...state, settingsDialogOpened: !state.settingsDialogOpened };
    }
    case types.TOGGLE_LEARN_MORE_DIALOG: {
      return { ...state, learnMoreDialogOpened: !state.learnMoreDialogOpened };
    }
    case types.TOGGLE_WORKER_DIALOG: {
      return { ...state, workerDialogOpened: !state.workerDialogOpened };
    }
    case types.SET_SIDE_BAR_OPENED: {
      return { ...state, isSideBarOpened: action.isSideBarIsOpened };
    }
    case types.TOGGLE_CREATE_UPDATE_PROJECT_DIALOG: {
      return { ...state, isCreateUpdateProjectOpened: !state.isCreateUpdateProjectOpened };
    }
    default:
      return state;
  }
}

export const actions = {
  goOnline: () => ({ type: types.DEVICE_ONLINE }),
  goOffline: () => ({ type: types.DEVICE_OFFLINE }),
  setUpdateAvailable: (isUpdateAvailable: boolean) => ({
    type: types.SET_NEW_VERSION_AVAILABLE,
    isUpdateAvailable,
  }),
  setSideBarIsOpen: (isSideBarIsOpened: boolean) => ({
    type: types.SET_SIDE_BAR_OPENED,
    isSideBarIsOpened,
  }),
  showSideBar: () => ({ type: types.SET_SIDE_BAR_OPENED, isSideBarIsOpened: false }),
  hideSideBar: () => ({ type: types.SET_SIDE_BAR_OPENED, isSideBarIsOpened: true }),
  showNotification: (
    text: string,
    notificationType: string = "default",
    autohide: boolean = true,
    severity: string
  ) => ({
    type: types.SET_NOTIFICATION_STATUS,
    visible: true,
    text,
    notificationType,
    autohide,
    severity,
  }),
  hideNotifications: () => ({
    type: types.SET_NOTIFICATION_STATUS,
    visible: false,
    text: null,
    notificationType: "default",
    autohide: true,
    severity: null,
  }),
  setLanguage: (language: string) => ({ type: types.SET_LANGUAGE, language }),
  setCountry: (country: string) => ({ type: types.SET_COUNTRY, country }),
  setCookies: (isCookie: boolean) => ({ type: types.SET_COOKIES, isCookie }),
  setLandLanguage: (languageISO: string) => (dispatch: (language: string) => void) => {
    // fetchLanguage('POST', { language: languageISO});
    dispatch(actions.setLanguage(languageISO));
  },
  setUserCountry: (country: string) => (dispatch: (country: string) => void) => {
    fetchLanguage("POST", { country });
    dispatch(actions.setCountry(country));
  },
  setNotification: (text: string) => (dispatch: (text: string) => void) => {
    dispatch(actions.showNotification(text));
  },
  setDisplaySizing: (sizing) => ({ type: types.SET_DISPLAYSIZING, sizing }),
  toggleAddUserDialog: () => ({ type: types.TOGGLE_ADD_USER_DIALOG }),
  toggleEditUserDialog: () => ({ type: types.TOGGLE_EDIT_USER_DIALOG }),
  toggleSettingsDialog: () => ({ type: types.TOGGLE_SETTINGS_DIALOG }),
  toggleLicenseDialog: () => ({ type: types.TOGGLE_LICENSE_DIALOG }),
  toggleCreateUpdateProject: () => ({ type: types.TOGGLE_CREATE_UPDATE_PROJECT_DIALOG }),
};

// Selectors
// export const isOnline = (state: Object) => state.app.isOnline;
export const isLoading = (state: Object) => state.app.isLoading;
export const isCreateUpdateProjectOpened = (state: Object) => state.app.isCreateUpdateProjectOpened;
export const getAppSettings = (state: Object) => state.app;
export const getSideBarIsOpened = (state: Object) => state.app.isSideBarOpened;
export const getNotificationStatus = (state: Object) => state.app.notificationStatus;
// export const getNotifications = (state: Object) => state.app.notifications;
export const getLanguage = (state: Object) => state.app.interfaceLanguage;
// export const getServicesLanguages = (state) => state.app.serviceSupportedLanguages;
// export const getDisplaySizing = (state: Object) => state.app.displayWidth;
// export const getCurrentUserCountry = (state: Object) => state.app.userCountry;
// export const getSupportedLanguages = (state: Object) => state.app.languages;
// export const getSupportedCountries = (state: Object) => state.app.supportedCountries;
// export const getMainSliderData = (state: Object) => state.app.mainSlider;
// export const getMainDataSlide = (state: Object) => state.app.dataSlide;
// export const isAddUserDialogOpened = (state: Object) => state.app.addUserDialogOpened;
// export const isEditUserDialogOpened = (state: Object) => state.app.editUserDialogOpened;
// export const isSettingsDialogOpened = (state: Object) => state.app.settingsDialogOpened;
// export const isLicenseDialogOpened = (state: Object) => state.app.licenseDialogOpened;
